import { ReactNode } from 'react';
import { useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { SidebarDemo } from '@/components/ui/sidebar-demo';
import { SidePanelProvider, useSidePanel } from '@/hooks/use-side-panel';

interface ShellProps {
  children: ReactNode;
  className?: string;
}

const getPageTitle = (path: string): string => {
  switch (path) {
    case '/':
      return 'Dashboard';
    case '/engagements':
      return 'Engagements';
    case '/time-logs':
      return 'Time Logs';
    case '/invoices':
      return 'Invoices';
    default:
      return 'Contraq';
  }
};

import { useMobile } from '@/hooks/use-mobile';

const ShellContent = ({ children, className }: ShellProps) => {
  const { isOpen, width } = useSidePanel();
  const isMobile = useMobile();

  const shouldResize = isOpen && !isMobile;

  return (
    <div className="min-h-screen bg-background flex h-screen overflow-hidden w-full">
      {/* Sidebar navigation */}
      <SidebarDemo />

      {/* Main Content Area */}
      <main
        className="flex-1 min-w-0 overflow-auto transition-all duration-300 ease-in-out"
        style={{
          marginRight: shouldResize ? `${width || 500}px` : '0',
        }}
      >
        <div className={cn(
          "p-4 md:p-6 pt-16 md:pt-4 min-h-screen w-full max-w-full",
          className
        )}>
          {children}
        </div>
      </main>
    </div>
  );
};

const Shell = ({ children, className }: ShellProps) => {
  const [location] = useLocation();
  const pageTitle = getPageTitle(location);
  const { user } = useAuth();

  // Check if current path is auth page
  const isAuthPage = location === '/auth';

  // If on auth page, render only the children without navigation
  if (isAuthPage || !user) {
    return (
      <div className="min-h-screen bg-background">
        {children}
      </div>
    );
  }

  // Layout with sidebar for authenticated users
  return (
    <SidePanelProvider>
      <ShellContent className={className}>
        {children}
      </ShellContent>
    </SidePanelProvider>
  );
};

export default Shell;
